<template>
  <Dialog :open="props.show" @update:open="onDialogUpdate">
    <DialogScrollContent size="md" class="tw:gap-6">
      <DialogHeader>
        <DialogTitle>{{$t("Edit member")}}</DialogTitle>
      </DialogHeader>
      <div class="tw:space-y-6">
        <div v-if="permission?.user">
          <h5 class="tw:text-base">{{ $t("Details") }}</h5>
          <div class="tw:bg-zinc-50/70 tw:border tw:border-zinc-200/50 tw:rounded tw:px-4">
            <dl
              class="tw:grid tw:grid-cols-1 tw:text-base/6 tw:sm:grid-cols-[min(30%,--spacing(80))_auto] tw:sm:text-sm/6">
              <template v-if="permission.user.firstname || permission.user.lastname">
                <dt
                  class="tw:col-start-1 tw:border-t tw:border-zinc-950/5 tw:pt-3 tw:text-zinc-500 tw:first:border-none tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:dark:border-white/5 tw:dark:text-zinc-400 tw:sm:dark:border-white/5">
                  {{$t('Name')}}
                </dt>
                <dd
                  class="tw:pt-1 tw:pb-3 tw:text-zinc-950 tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:sm:nth-2:border-none tw:dark:text-white tw:dark:sm:border-white/5">
                  {{ permission.user.firstname }} <span class="text-uppercase">{{ permission.user.lastname }}</span>
                </dd>
              </template>
              <template v-if="permission.user.email">
                <dt
                  class="tw:col-start-1 tw:border-t tw:border-zinc-950/5 tw:pt-3 tw:text-zinc-500 tw:first:border-none tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:dark:border-white/5 tw:dark:text-zinc-400 tw:sm:dark:border-white/5">
                  {{$t('Email')}}
                </dt>
                <dd
                  class="tw:pt-1 tw:pb-3 tw:text-zinc-950 tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:sm:nth-2:border-none tw:dark:text-white tw:dark:sm:border-white/5 tw:break-all">
                  {{ permission.user.email }}
                  <button type="button" @click="copyToClipboard(permission.user.email)" class="tw:hover:bg-zinc-200 tw:rounded tw:aspect-square tw:px-1 tw:ml-1">
                    <i class="fa fa-clipboard"></i>
                  </button>
                </dd>
              </template>
              <template v-if="permission.user.company">
                <dt
                  class="tw:col-start-1 tw:border-t tw:border-zinc-950/5 tw:pt-3 tw:text-zinc-500 tw:first:border-none tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:dark:border-white/5 tw:dark:text-zinc-400 tw:sm:dark:border-white/5">
                  {{$t('Company')}}
                </dt>
                <dd
                  class="tw:pt-1 tw:pb-3 tw:text-zinc-950 tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:sm:nth-2:border-none tw:dark:text-white tw:dark:sm:border-white/5">
                  {{ permission.user.company }}
                </dd>
              </template>
            </dl>
          </div>
        </div>
        <div class="tw:space-y-2">
          <h5 class="tw:text-base">{{ $t("Access Type") }}</h5>
          <ul class="list-group list-group-flush tw:bg-zinc-50/50 tw:border tw:border-zinc-200/50 tw:rounded tw:px-4">
            <li class="list-group-item d-flex align-items-center px-0 tw:justify-between tw:bg-transparent">
              <label for="is-owner">
                {{ $t("Administrator") }}
              </label>
              <div>
                <Toggle class="tw:mt-1" id="is-owner" v-model="is_owner" />
              </div>
            </li>
          </ul>
        </div>
        <template v-if="!is_owner">
          <div class="tw:space-y-2">
            <h5 class="tw:text-base">{{ $t("Permissions") }}</h5>
            <ul
              class="list-group list-group-flush overflow-auto tw:max-h-[20.7rem] tw:bg-zinc-50/70 tw:border tw:border-zinc-200/50 tw:rounded tw:px-4">
              <li v-for="ability in abilities"
                class="list-group-item d-flex tw:items-start tw:gap-4 px-0 tw:justify-between tw:bg-transparent tw:border-zinc-200/50">
                <label :for="`permission-${ability.value}`">
                  <div>{{ $t(ability.label) }}</div>
                  <template v-if="ability.description">
                    <div class="text-muted tw:text-sm">
                      {{ $t(ability.description) }}
                    </div>
                  </template>
                </label>
                <div>
                  <Toggle
                    :id="`permission-${ability.value}`"
                    class="tw:mt-1"
                    :modelValue="
                      Array.isArray(selectedAbilities) &&
                      selectedAbilities.indexOf(ability.value) >= 0
                    "
                    :disabled="is_owner"
                    @update:modelValue="changeAbility(ability.value, $event)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </template>
        <Alert variant="danger" v-if="error">
          {{ error }}
        </Alert>
      </div>
      <DialogFooter>
        <button type="button" class="btn btn-outline-danger mr-auto" v-on:click.prevent="showDeleteConfirm = true">
          {{ $t("Remove member") }}
        </button>
        <button type="button" class="btn btn-secondary" v-on:click="emits('closed')">
          {{ $t("Cancel") }}
        </button>
        <button type="button" class="btn btn-primary" v-on:click.prevent="savePermission()">
          {{ $t("Save") }}
        </button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <AlertDialog :open="showDeleteConfirm" @update:open="showDeleteConfirm = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t("Are you sure?") }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ $t("This action cannot be undone. This will permanently remove this member’s access.") }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <button type="button" class="btn btn-secondary" @click="showDeleteConfirm = false">
          {{ $t("Cancel") }}
        </button>
        <button type="button" class="btn btn-danger" @click="confirmDelete">
          {{ $t("Delete") }}
        </button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup>
import { ref } from "vue";
import { useClipboard } from '@vueuse/core'
import Toggle from "@/components/ui/Toggle.vue";
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/shadcn/components/ui/alert-dialog";
import { watch } from "vue";
import Alert from "@/components/ui/Alert.vue";

const props = defineProps({
  abilities: {
    default: () => [],
  },
  permission: {
    default: () => {},
  },
  show: {
    default: false,
  },
  error: {
    default: null,
  },
});

const dialog = ref(null);
const selectedAbilities = ref([]);
const is_owner = ref(false);
const emits = defineEmits(["save", "delete", "closed"]);

const showDeleteConfirm = ref(false);

watch(
  () => props.show,
  (value) => {
    if (value) {
      setData();
    }
  },
);

const onDialogUpdate = (value) => {
  if (value) {
    setData();
  } else {
    emits("closed");
  }
};

const { copy: copyToClipboard } = useClipboard()

const setData = () => {
  selectedAbilities.value = [...(props.permission.abilities ?? [])];
  is_owner.value = props.permission.is_owner ?? false;
};

const changeAbility = (ability, isSelect) => {
  if (isSelect) {
    selectedAbilities.value.push(ability);
  } else {
    let index = selectedAbilities.value.indexOf(ability);
    selectedAbilities.value.splice(index, 1);
  }
};

const savePermission = () => {
  const permissionToSave = {
    ...props.permission,
    abilities: selectedAbilities.value,
    is_owner: is_owner.value,
  };

  emits("save", permissionToSave);
};

const confirmDelete = () => {
  showDeleteConfirm.value = false;
  emits("delete");
};
</script>
